import { useMemo } from 'react';
import type { Setup } from '@/types';
import type { UseTuningSession } from '../hooks/useTuningSession';
import { getTuningSpec } from '../data/bow-tuning-specs';
import { BOWS } from '@/data/bows';
import { Card } from '@/components/ui/Card';
import { GoalCard, TipCard, WarningCard, InfoCard } from '../components/Cards';

interface TillerData {
  /** Measured distance from TOP limb bolt to string at rest, in inches. */
  topMeasurement?: number;
  /** Measured distance from BOTTOM limb bolt to string at rest, in inches. */
  bottomMeasurement?: number;
}

interface Props {
  tuning: UseTuningSession;
  setup: Setup;
}

/**
 * Step 3 — Tiller
 *
 * Measures top vs bottom limb-to-string distance. Difference = tiller.
 *   positive tiller (top > bottom): top limb has more pre-load, normal for finger shooters
 *   zero tiller: matched limbs — preferred for most compound release shooters
 *   negative tiller (top < bottom): bottom limb stiffer — unusual
 *
 * Adjustment: 1/2 turn of a limb bolt ≈ 1/16" tiller shift ≈ 1 lb draw weight.
 * To shift tiller without changing total DW: loosen one bolt the same amount you
 * tighten the other.
 */
export function TillerStep({ tuning }: Props) {
  const preflight = (tuning.session.stepData['preflight'] as
    | { bowBrand?: string; bowModel?: string; bowYear?: number }
    | undefined) ?? {};
  const data = (tuning.session.stepData['tiller'] as TillerData | undefined) ?? {};
  const update = (patch: Partial<TillerData>) =>
    tuning.updateStepData<TillerData>('tiller', { ...data, ...patch });

  const bowEntry = useMemo(
    () =>
      preflight.bowBrand && preflight.bowModel && preflight.bowYear
        ? BOWS.find(
            (b) =>
              b.brand === preflight.bowBrand &&
              b.model === preflight.bowModel &&
              b.year === preflight.bowYear,
          )
        : undefined,
    [preflight.bowBrand, preflight.bowModel, preflight.bowYear],
  );
  const tuningSpec = useMemo(
    () => (bowEntry ? getTuningSpec(bowEntry.brand, bowEntry.model, bowEntry.year) : null),
    [bowEntry],
  );

  const target = tuningSpec?.tillerTarget ?? 0;
  const tolerance = tuningSpec?.tillerTolerance ?? 0.125;

  const tiller =
    data.topMeasurement !== undefined && data.bottomMeasurement !== undefined
      ? data.topMeasurement - data.bottomMeasurement
      : null;
  const deltaFromTarget = tiller !== null ? Math.abs(tiller - target) : null;
  const inSpec = deltaFromTarget !== null && deltaFromTarget <= tolerance;
  const offTooMuch = deltaFromTarget !== null && deltaFromTarget > tolerance;

  return (
    <>
      <GoalCard title="What we're trying to achieve">
        Top and bottom limbs should be matched (or as close as your bow's spec calls for).
        Equal pre-load means equal limb travel = symmetric arrow launch = clean paper tear.
      </GoalCard>

      <Card padding="lg">
        <h3 className="font-semibold mb-3">Measure tiller</h3>
        <p className="text-[12px] text-zinc-400 mb-4 leading-relaxed">
          With the bow at rest (brace, not drawn), measure from the perpendicular face of
          each limb pocket to the string. Use a tiller gauge or a precise ruler held flush
          against the limb at the bolt.
        </p>

        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Top limb (in)">
            <input
              type="number"
              step={0.0625}
              value={data.topMeasurement ?? ''}
              onChange={(e) => update({ topMeasurement: parseFloatOrUndef(e.target.value) })}
              placeholder="e.g. 7.125"
              className={inputClass}
            />
          </Field>
          <Field label="Bottom limb (in)">
            <input
              type="number"
              step={0.0625}
              value={data.bottomMeasurement ?? ''}
              onChange={(e) => update({ bottomMeasurement: parseFloatOrUndef(e.target.value) })}
              placeholder="e.g. 7.125"
              className={inputClass}
            />
          </Field>
        </div>

        {tiller !== null && (
          <div className="mt-4 grid sm:grid-cols-3 gap-3">
            <Readout label="Your tiller" value={formatTiller(tiller)} />
            <Readout label="Spec target" value={`${target === 0 ? '0" (matched)' : `${target}"`}`} />
            <Readout
              label="Status"
              value={inSpec ? '✓ in spec' : '✗ out of spec'}
              color={inSpec ? 'emerald' : 'rose'}
            />
          </div>
        )}
      </Card>

      {inSpec && (
        <div className="rounded-lg border border-emerald-700/40 bg-emerald-950/20 p-3 flex gap-2.5">
          <div className="text-emerald-400 text-lg">✓</div>
          <div className="text-sm">
            <p className="font-semibold text-emerald-300">Tiller is in spec.</p>
            <p className="text-zinc-300 mt-0.5">
              Move on to center shot. If walk-back tune later shows vertical drift, come back
              here and re-measure — tiller can shift as cables creep.
            </p>
          </div>
        </div>
      )}

      {offTooMuch && tiller !== null && (
        <TipCard
          title={`Fix: tiller is ${tiller > target ? 'POSITIVE' : 'NEGATIVE'} of spec`}
          defaultOpen
        >
          <p className="mb-2">
            <strong>What this means:</strong>{' '}
            {tiller > target
              ? 'Top limb has more pre-load (sits "tighter") than bottom. Often shows as a tail-low paper tear and as vertical drift at distance.'
              : 'Bottom limb has more pre-load than top. Often shows as a tail-high paper tear.'}
          </p>
          <p className="font-semibold text-zinc-100 mb-1">To shift tiller without changing total DW:</p>
          <ol className="list-decimal list-inside space-y-1 mb-3 text-zinc-300">
            <li>
              {tiller > target
                ? 'LOOSEN top limb bolt 1/2 turn (= roughly 1/16" tiller shift, also drops DW ~1 lb)'
                : 'LOOSEN bottom limb bolt 1/2 turn'}
            </li>
            <li>
              {tiller > target
                ? 'TIGHTEN bottom limb bolt 1/2 turn (gains back the 1 lb)'
                : 'TIGHTEN top limb bolt 1/2 turn'}
            </li>
            <li>Re-measure both limbs. Repeat in 1/2-turn steps.</li>
          </ol>
          <p className="text-[11px] text-zinc-400 mb-1">
            <strong>Bolt-out safety limit:</strong> never back a limb bolt out further than the
            manufacturer's spec (usually 4-6 turns from bottomed). Each turn shifts ~2 lb DW.
          </p>
          <p className="text-[11px] text-zinc-400">
            <strong>Symmetry rule:</strong> keep both bolts within 1 turn of each other to keep
            limb deflection symmetric.
          </p>
        </TipCard>
      )}

      <InfoCard title="What is tiller, really?">
        <p className="mb-2">
          Tiller is the difference between the top and bottom limb pre-load. Equal pre-load
          (zero tiller) means both limbs flex and return the same amount during the shot,
          launching the arrow symmetrically.
        </p>
        <p className="mb-2">
          Older recurve archers ran positive tiller (top tighter) for finger-shot
          consistency. Modern compound release shooters target zero tiller — but Mathews,
          Hoyt, PSE, and Bowtech all spec their bows close to zero out of the box.
        </p>
        <p>
          <strong>Tiller drift</strong> usually means cables have stretched unevenly. If you
          re-tiller and it goes back out of spec a few weeks later, your strings need replacing.
        </p>
      </InfoCard>

      <WarningCard title="Safety reminder">
        Turn limb bolts in small increments only. Always verify total draw weight after
        adjusting tiller — if it changed significantly, you may have backed a bolt out past
        the safe limit. Never exceed your bow's max DW spec.
      </WarningCard>
    </>
  );
}

// ─── helpers ───

const inputClass =
  'w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-ember-500';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-[11px] text-zinc-500 block mb-1">{label}</label>
      {children}
    </div>
  );
}

function Readout({ label, value, color }: { label: string; value: string; color?: 'emerald' | 'rose' }) {
  return (
    <div
      className={
        'rounded-md border p-2.5 ' +
        (color === 'emerald'
          ? 'border-emerald-700/40 bg-emerald-950/20'
          : color === 'rose'
            ? 'border-rose-700/40 bg-rose-950/20'
            : 'border-zinc-800 bg-zinc-950/50')
      }
    >
      <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-0.5">
        {label}
      </p>
      <p
        className={
          'text-sm font-semibold ' +
          (color === 'emerald'
            ? 'text-emerald-300'
            : color === 'rose'
              ? 'text-rose-300'
              : 'text-zinc-100')
        }
      >
        {value}
      </p>
    </div>
  );
}

function parseFloatOrUndef(s: string): number | undefined {
  if (s === '') return undefined;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : undefined;
}

function formatTiller(t: number): string {
  // Express as e.g. "+1/8\"" or "-1/16\"" or "matched (0)"
  if (Math.abs(t) < 0.001) return 'matched (0)';
  const sign = t > 0 ? '+' : '-';
  return `${sign}${formatInchesAsFrac(Math.abs(t))}`;
}

function formatInchesAsFrac(inches: number): string {
  const sixteenths = Math.round(inches * 16);
  if (sixteenths === 0) return '0"';
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
  const g = gcd(sixteenths, 16);
  return `${sixteenths / g}/${16 / g}"`;
}
